import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Container } from 'src/components/Container';
import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { PlatformTab } from 'src/models/platform/_types';
import { fetchUserByHash } from 'src/models/user/user';
import { NotificationsLayout } from 'src/shared/pageComponents/Notifications/NotificationsLayout';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';

const NotificationsUnsubscribeAuthed: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'notifications' });
  const { user } = useOptionalSession();
  const { canViewTrainerSidebarTabs } = usePermissionCheck();

  if (!user?.id) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('standaloneManagingFor')}
        </Typography>
        <Typography variant="h6">
          {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
            user.nickname ||
            user.email}
        </Typography>
        {user.email ? (
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        ) : null}
      </Stack>
      <NotificationsLayout
        showCustomizeEmailsLink={canViewTrainerSidebarTabs({
          id: 'Emails',
        } as PlatformTab)}
        standalone
      />
    </Container>
  );
};

const NotificationsUnsubscribeGuest: FC<{ hashFromUrl: string }> = ({
  hashFromUrl,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'notifications' });

  const {
    data: recipient,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['userByHash', hashFromUrl],
    queryFn: () => fetchUserByHash(hashFromUrl),
    retry: false,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          minHeight: '40vh',
        }}
      >
        <Loader size="large" />
      </Box>
    );
  }

  if (isError || !recipient) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Typography variant="h6" gutterBottom>
          {t('standaloneInvalidLink')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('standaloneSignInHint')}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('standaloneManagingFor')}
        </Typography>
        <Typography variant="h6">{recipient.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {recipient.email}
        </Typography>
      </Stack>
      <NotificationsLayout
        hashid={hashFromUrl}
        recipientUser={recipient}
        showCustomizeEmailsLink={false}
        standalone
      />
    </Container>
  );
};

const NotificationsUnsubscribePage: FC = () => {
  const { user } = useOptionalSession();
  const [searchParams] = useSearchParams();
  const hashFromUrl = searchParams.get('hash');

  if (user?.id) {
    return <NotificationsUnsubscribeAuthed />;
  }

  if (!hashFromUrl) {
    return (
      <Navigate
        to={`/login?redirect_to=${encodeURIComponent('/ex4/me/notifications')}`}
        replace
      />
    );
  }

  return <NotificationsUnsubscribeGuest hashFromUrl={hashFromUrl} />;
};

export default NotificationsUnsubscribePage;
